import { StateBlock } from "@/components/layout-2/ds";
import { FilterChip, ObjectChoice } from "@/components/layout-2/ds-settings";
import type { FilterTree } from "@/components/layout-2/filter-tree/types";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Check, Search } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { feedingClient } from "./api";
import { recordsLabel } from "./counts";
import { describeFilters, describeTree, useFilterDefinitions } from "./filter-chips";
import type { DRQ, FeedingObject } from "./types";
import { useLoad } from "./use-load";

type ListedView = { id: number; name: string; object: FeedingObject; owner: string | null; mine?: boolean; filters?: DRQ; filter_logic?: string | null; filter_tree?: FilterTree | null };

/** The size of the selected ListView: loading, counted by the server, or `null` when it could not be (error, timeout). */
type Count = "loading" | { records: number; records_limit: number } | null;

/** A standard ListView as the picker hands it over. `records` comes from the count of the selection, not from the list. */
export type ListViewOption = ListedView & { records: number | null; records_limit: number };

/** A row the caller puts before the ListViews ("No filter"): picked, it hands over `null`. */
export type PickerExtra = { value: string; label: string; description: string };

/** Chips shown per row before "+N". */
const CHIPS = 2;

/**
 * Picks a standard ListView, in the body of a step dialog (never a dialog of its own). With
 * `onObjectChange`, the object is chosen first, as identity tiles (an exclusion); the ListViews of the
 * object follow, grouped as the user's and the shared ones, each with its filters as chips and, once
 * selected, its size.
 */
export function ListViewPicker({
    campaignCode,
    objects,
    objectType,
    onObjectChange,
    objectHint,
    extra,
    onExtra,
    onChange,
    emptyHint
}: {
    campaignCode: string;
    objects: FeedingObject[];
    objectType: string;
    onObjectChange?: (type: string) => void;
    /** Under the tiles: what choosing this object means. */
    objectHint?: React.ReactNode;
    /** A first row that is not a ListView, and what to do when it is picked. */
    extra?: PickerExtra;
    onExtra?: () => void;
    /** The selected ListView with its count once it arrived, or null. */
    onChange: (view: ListViewOption | null) => void;
    /** Added to "No standard ListView exists". */
    emptyHint?: string;
}) {
    const [selected, setSelected] = useState<number | "extra" | null>(null);
    const [query, setQuery] = useState("");
    const [counts, setCounts] = useState<Record<number, Count>>({});
    const object = objects.find(item => item.type === objectType);
    const definitions = useFilterDefinitions(object?.url);

    const { data, error, loading, reload } = useLoad<ListedView[]>(
        () => feedingClient(campaignCode, "list-view-options/").query({ object_type: objectType }).get().json<ListedView[]>(),
        [campaignCode, objectType],
        "Unable to load the ListViews."
    );
    // Another object is another list: the selection does not carry over.
    useEffect(() => setSelected(null), [objectType]);

    // Only the selected ListView is counted, once: the list itself counts nothing.
    useEffect(() => {
        if (typeof selected !== "number" || counts[selected] !== undefined) return;
        const id = selected;
        setCounts(previous => ({ ...previous, [id]: "loading" }));
        feedingClient(campaignCode, `list-view-options/${id}/count/`)
            .get()
            .json<{ records: number; records_limit: number }>()
            .then(count => setCounts(previous => ({ ...previous, [id]: typeof count?.records === "number" ? count : null })))
            .catch(() => setCounts(previous => ({ ...previous, [id]: null })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, campaignCode]);

    const view = typeof selected === "number" ? (data?.find(item => item.id === selected) ?? null) : null;
    const count = typeof selected === "number" ? counts[selected] : undefined;
    useEffect(() => {
        if (selected === "extra") return;
        if (!view) return onChange(null);
        const known = count && count !== "loading" ? count : null;
        onChange({ ...view, records: known?.records ?? null, records_limit: known?.records_limit ?? 0 });
    }, [view, count, selected]); // eslint-disable-line react-hooks/exhaustive-deps

    const countLabel = (id: number): string | undefined => {
        if (id !== selected || count === undefined) return undefined;
        if (count === "loading") return "Counting…";
        return count === null ? "—" : (recordsLabel(count.records, count.records_limit) ?? "—");
    };
    const chipsOf = (item: ListedView) => (item.filter_tree ? describeTree(item.filter_tree) : describeFilters(item.filters, definitions));
    const shown = (data ?? []).filter(item => item.name.toLowerCase().includes(query.toLowerCase()));
    const groups = [
        { label: "Yours", rows: shown.filter(item => item.mine) },
        { label: "Shared with you", rows: shown.filter(item => !item.mine) }
    ].filter(group => group.rows.length > 0);

    return (
        <div className="flex flex-col gap-3" data-testid="list-view-picker">
            {onObjectChange && (
                <div className="flex flex-col gap-1.5">
                    <ObjectChoice
                        label="Object of the ListView"
                        value={objectType}
                        onChange={onObjectChange}
                        items={objects.map(item => ({ value: item.type, label: item.label, icon: item.icon, color: item.color }))}
                    />
                    {objectHint && <p className="text-xs text-gray-600">{objectHint}</p>}
                </div>
            )}
            <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" aria-hidden />
                <Input className="pl-9" placeholder={`Search ${object?.label.toLowerCase() ?? ""} ListViews…`} value={query} onChange={event => setQuery(event.target.value)} aria-label="Search a ListView" />
            </div>
            {loading || error ? (
                <StateBlock kind={loading ? "loading" : "error"} message={error ?? undefined} onRetry={reload} />
            ) : (
                <div role="radiogroup" aria-label="Standard ListViews" className="max-h-[22rem] overflow-y-auto rounded-md border" data-testid="option-list-scroll">
                    {extra && (
                        <PickerRow
                            testId="list-view-option-extra"
                            selected={selected === "extra"}
                            onPick={() => {
                                setSelected("extra");
                                onChange(null);
                                onExtra?.();
                            }}
                            title={extra.label}
                            detail={<span className="text-xs text-gray-500">{extra.description}</span>}
                        />
                    )}
                    {data?.length === 0 ? (
                        <StateBlock message={`No standard ListView exists on ${object?.label ?? objectType}.${emptyHint ? ` ${emptyHint}` : ""}`} />
                    ) : shown.length === 0 ? (
                        <StateBlock message={`No ListView matches “${query}”.`} />
                    ) : (
                        groups.map(group => (
                            <Fragment key={group.label}>
                                <div className="bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600">{group.label}</div>
                                {group.rows.map(item => {
                                    const chips = chipsOf(item);
                                    return (
                                        <PickerRow
                                            key={item.id}
                                            testId={`list-view-option-${item.id}`}
                                            selected={selected === item.id}
                                            onPick={() => setSelected(item.id)}
                                            title={item.name}
                                            meta={countLabel(item.id)}
                                            detail={
                                                <span className="flex flex-wrap items-center gap-1">
                                                    {chips.length === 0 ? (
                                                        <span className="text-xs text-amber-800">No filter</span>
                                                    ) : (
                                                        chips.slice(0, CHIPS).map((chip, index) => <FilterChip key={index} label={chip.label} value={chip.value} />)
                                                    )}
                                                    {chips.length > CHIPS && <FilterChip label={`+${chips.length - CHIPS}`} />}
                                                    {!item.mine && item.owner && <span className="text-xs text-gray-500">· {item.owner}</span>}
                                                </span>
                                            }
                                        />
                                    );
                                })}
                            </Fragment>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

function PickerRow({ selected, onPick, title, detail, meta, testId }: { selected: boolean; onPick: () => void; title: string; detail?: React.ReactNode; meta?: string; testId: string }) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={onPick}
            data-testid={testId}
            className={cn(
                "grid w-full grid-cols-[minmax(0,1fr)_auto_1.25rem] items-center gap-3 border-b px-3 py-2.5 text-left text-sm last:border-b-0 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                selected && "bg-denim-blue/10 hover:bg-denim-blue/10"
            )}
        >
            <span className="flex min-w-0 flex-col gap-1">
                <span className="truncate font-semibold text-black">{title}</span>
                {detail}
            </span>
            <span className="text-xs tabular-nums text-gray-600">{meta}</span>
            <span className={cn("flex h-5 w-5 items-center justify-center rounded-full", selected ? "bg-denim-blue text-white" : "border border-gray-300")} aria-hidden>
                {selected && <Check className="h-3 w-3" />}
            </span>
        </button>
    );
}
